import { Box, Typography, TextField, Button, Stack, Paper, Alert } from '@mui/material';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { submitContactForm, selectContact, resetContactState } from '../../../store/slices/contactSlice';
import { useTheme as useAppTheme } from '../../../contexts/ThemeContext';

const ContactUsSection = () => {
    const { mode } = useAppTheme();
    const dispatch = useAppDispatch();
    const { loading, error, success } = useAppSelector(selectContact);
    
    const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
    const [errors, setErrors] = useState({ name: false, email: false, phone: false, subject: false, message: false });

    // Reset contact state when component unmounts
    useEffect(() => {
        return () => {
            dispatch(resetContactState());
        };
    }, [dispatch]);

    // Auto-reset success state after 5 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                dispatch(resetContactState());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, dispatch]);

    const validate = () => {
        const newErrors = {
            name: !form.name.trim(),
            email: !/^\S+@\S+\.\S+$/.test(form.email),
            phone: !form.phone.trim() || form.phone.length > 11 || !/^\d+$/.test(form.phone),
            subject: !form.subject.trim(),
            message: !form.message.trim(),
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(Boolean);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        
        // Phone number validation - only allow digits and max 11 characters
        if (name === 'phone') {
            const numericValue = value.replace(/\D/g, ''); // Remove non-digits
            if (numericValue.length <= 11) {
                setForm({ ...form, [name]: numericValue });
            }
        } else {
            setForm({ ...form, [name]: value });
        }
        
        // Clear error when user starts typing
        if (errors[name as keyof typeof errors]) {
            setErrors({ ...errors, [name]: false });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            await dispatch(submitContactForm(form));
            if (success) {
                setForm({ name: '', email: '', phone: '', subject: '', message: '' });
            }
        }
    };

    // Dynamic background color based on theme
    const getBackgroundColor = () => {
        return mode === 'dark' ? '#111112' : '#f8f9fa';
    };

    return (
        <Box sx={{ 
            bgcolor: getBackgroundColor(), 
            py: { xs: 8, md: 12 }, 
            px: { xs: 1, md: 0 }, 
            width: '100%' 
        }}>
            <Paper
                component={motion.div}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, ease: [0.4, 0.1, 0.2, 1] }}
                elevation={3}
                sx={{
                    maxWidth: '90%',
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    overflow: 'hidden',
                    borderRadius: 5,
                }}
            >
                {/* Left: Contact Form */}
                <Box sx={{ flex: 1, p: { xs: 3, md: 5 }, bgcolor: 'background.paper' }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: 'secondary.main' }}>
                        Contact Us
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', mb: 4 }}>
                        Have a question or want to work with us? Fill out the form and our team will get back to you soon.
                    </Typography>
                    
                    {/* Success/Error Messages */}
                    {success && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Thank you! Your message has been sent successfully. We'll get back to you soon.
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}
                    
                    <form onSubmit={handleSubmit} autoComplete="off">
                        <Stack spacing={3}>
                            <TextField
                                label="Name"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                error={errors.name}
                                helperText={errors.name && 'Name is required'}
                                fullWidth
                                variant="outlined"
                                disabled={loading === 'pending'}
                            />
                            <TextField
                                label="Email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                error={errors.email}
                                helperText={errors.email && 'Enter a valid email'}
                                fullWidth
                                variant="outlined"
                                disabled={loading === 'pending'}
                            />
                            <TextField
                                label="Phone"
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                error={errors.phone}
                                helperText={errors.phone && (form.phone.length < 11 || form.phone.length > 11 ? 'Phone number must be 11 digits' : 'Phone number is required')}
                                fullWidth
                                variant="outlined"
                                disabled={loading === 'pending'}
                                placeholder="e.g., 03001234567"
                            />
                            <TextField
                                label="Subject"
                                name="subject"
                                value={form.subject}
                                onChange={handleChange}
                                error={errors.subject}
                                helperText={errors.subject && 'Subject is required'}
                                fullWidth
                                variant="outlined"
                                disabled={loading === 'pending'}
                            />
                            <TextField
                                label="Message"
                                name="message"
                                value={form.message}
                                onChange={handleChange}
                                error={errors.message}
                                helperText={errors.message && 'Message is required'}
                                fullWidth
                                multiline
                                minRows={4}
                                variant="outlined"
                                disabled={loading === 'pending'}
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                color="secondary"
                                size="large"
                                sx={{ fontWeight: 700, borderRadius: 2, mt: 1 }}
                                disabled={loading === 'pending'}
                            >
                                {loading === 'pending' ? 'Sending...' : 'Send Message'}
                            </Button>
                        </Stack>
                    </form>
                </Box>
                {/* Right: Company Info */}
                <Box sx={{ flex: 1, p: { xs: 3, md: 5 }, bgcolor: 'grey.900', color: 'grey.100', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <LocationOnIcon sx={{ color: 'secondary.main', fontSize: 32 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 700 }}>Our Office</Typography>
                            <Typography>37-PV Paradise commercials, Ferozepur Road near Gajjumata, Lahore</Typography>
                        </Box>
                    </Stack>
                    {/* <Stack direction="row" alignItems="center" spacing={2}>
                        <PhoneIcon sx={{ color: 'secondary.main', fontSize: 32 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 700 }}>Phone</Typography>
                            <Typography>(021) 111-222-333</Typography>
                        </Box>
                    </Stack> */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <EmailIcon sx={{ color: 'secondary.main', fontSize: 32 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 700 }}>Email</Typography>
                            <Typography>contact@citycourier.pk</Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <AccessTimeIcon sx={{ color: 'secondary.main', fontSize: 32 }} />
                        <Box>
                            <Typography sx={{ fontWeight: 700 }}>Working Hours</Typography>
                            <Typography>Mon - Sat: 09:00AM - 05:30PM</Typography>
                        </Box>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
};

export default ContactUsSection; 